import React from "react";
import { Box } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
import {
  findSystemBySubId,
  mainConsultationSystems,
  isSystemApproved,
  ENERGY_TRAINING_SUB_ID,
} from "./systemMapping";
import { getCategoryColor } from "../../../platform/store/helper";

// `stepper` is still pulled from context to read the current sub-step (for
// highlighting the active row). The rows themselves no longer dispatch
// navigation — that lives entirely in `ConsultationStepper`'s controls.

type RowProps = {
  active: boolean;
  label: string;
  score: number | null;
  scoreCategory: number | null;
};

const SidebarRow: React.FC<RowProps> = ({
  active,
  label,
  score,
  scoreCategory,
}) => {
  const theme: Theme = useTheme();
  // Non-interactive: system navigation happens via the stepper at the
  // bottom. The row only reflects the active state of the current sub-step.
  return (
    <Box sx={styles.row(theme, active)}>
      <Box component="span" sx={styles.rowLabel(theme)}>
        {label}
      </Box>
      {score !== null && (
        <Box
          component="span"
          sx={styles.rowScore(
            theme,
            getCategoryColor("%", score, theme, scoreCategory ?? undefined)
          )}
        >
          {score}%
        </Box>
      )}
    </Box>
  );
};

const SystemsSidebar: React.FC = () => {
  const theme: Theme = useTheme();
  const { data, stepper } = useConsultation();
  const currentSubId = stepper.current.sub.id;

  // Filter the sidebar to match the available sub-steps:
  //   • Body Composition row hidden when `hasInBody` is false (matches
  //     `useAvailableSections` and the score screen's sidebar filter).
  //   • Other main systems always shown.
  const visibleSystems = mainConsultationSystems(data.systems ?? []).filter(
    ({ subId }) => (subId === "body-composition" ? data.hasInBody : true)
  );

  // Energy & Training is synthesized (no backend system row). Hide entirely
  // when no zone data is available — matches the sub-step filter so the
  // sidebar list always lines up with the stepper.
  const vo2 = data.dashboard?.metrics?.find((m) =>
    m.id?.toLowerCase().includes("vo2")
  );
  const energyScore =
    typeof vo2?.score === "number" ? vo2.score : null;
  const energyCategory =
    typeof vo2?.scoreCategory === "number" ? vo2.scoreCategory : null;

  return (
    <Box component="aside" sx={styles.sidebar}>
      <Box sx={styles.header(theme)}>Your systems</Box>
      <Box sx={styles.list}>
        {visibleSystems.map(({ subId, system }) => {
          // Mirror the score screen: only show the system score when it's
          // fully approved. Unapproved systems render the row label alone.
          const approved = isSystemApproved(system);
          return (
            <SidebarRow
              key={subId}
              active={subId === currentSubId}
              label={system.name}
              score={
                approved && typeof system.score === "number"
                  ? system.score
                  : null
              }
              scoreCategory={
                approved && typeof system.scoreCategory === "number"
                  ? system.scoreCategory
                  : null
              }
            />
          );
        })}
        {data.hasEnergyTraining && (
          <SidebarRow
            active={ENERGY_TRAINING_SUB_ID === currentSubId}
            label="Energy & Training"
            score={energyScore}
            scoreCategory={energyCategory}
          />
        )}
      </Box>
    </Box>
  );
};

export default SystemsSidebar;
export { findSystemBySubId };

const styles = {
  sidebar: {
    width: "270px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  header: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    letterSpacing: "-0.28px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  list: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  row: (theme: Theme, active: boolean) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    height: "48px",
    padding: "8px 12px",
    borderRadius: "8px",
    border: `1px solid ${
      active
        ? theme.colors_ds.PRIMARY_TEXT_MAIN
        : theme.colors_ds.NEUTRAL_BORDER_WEAK
    }`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    boxShadow: active
      ? "0px 8px 10px rgba(0,0,0,0.06), 0px 2px 4px rgba(0,0,0,0.04)"
      : "none",
    textAlign: "left" as const,
  }),
  rowLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  rowScore: (theme: Theme, color: string) => ({
    backgroundColor: theme.colorTokens.N[30],
    borderRadius: "4px",
    padding: "4px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "12px",
    letterSpacing: "-0.13px",
    color,
  }),
};
